const libFableServiceProviderBase = require('fable-serviceproviderbase');

const libQRCodeMinter = require('headlight-qrcode/source/Headlight-QRCode-Minter.js');
const libQRCodeProfiles = require('headlight-qrcode/source/Headlight-QRCode-Profiles.js');

/**
 * HeadLight QR code generation for browser apps (and anything else that registers this service).
 *
 * Generation is delegated to headlight-qrcode's minter, which never returns the same code twice
 * from one service instance. The legacy formats keep their exact shape.
 */
class HeadlightQRCodeHash extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The fable instance
	 * @param {any} [pOptions] - The options for the service
	 * @param {any} [pServiceHash] - The hash for the service instance
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'HeadlightQRCodeHash';

		this.minter = new libQRCodeMinter();
		/** @type {null|ReturnType<typeof libQRCodeProfiles.resolveProfile>} */
		this.customerProfile = null;
		/** @type {Record<string, Promise<any>>} */
		this.customerProfileLoads = {};
	}

	/**
	 * A generic method to generate a GUID (unique id) string value out of alphanumeric
	 * characters. The default length is 10 characters long but can be customized.
	 * @param {number} [prefix] - an optional prefix, used to make the scope more clear
	 * @param {number} [length] - the number of characters in the return string
	 * @return {string} - a generated id string
	 */
	_generateGUID(prefix, length)
	{
		const alphanumericString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		length = length ? length : 10;
		let guid = '';
		for (let i = 0; i < length; i++)
		{
			guid += alphanumericString.charAt(Math.floor(Math.random() * alphanumericString.length));
		}
		guid = prefix ? prefix + guid : guid;
		return guid;
	}

	/**
	 * The timezone the app's fable Dates service renders in (pict forms set it to the project's
	 * zone), so legacy codes keep using it; undefined means the runtime's local zone.
	 *
	 * @return {string|undefined}
	 */
	_getConfiguredTimeZone()
	{
		try
		{
			const tmpZone = this.fable && this.fable.Dates && this.fable.Dates.dayJS && this.fable.Dates.dayJS().tz().$x.$timezone;
			return (typeof tmpZone === 'string' && tmpZone) ? tmpZone : undefined;
		}
		catch (pError)
		{
			return undefined;
		}
	}

	/**
	 * Generates QR Codes in the pre-2026 web format, takes a project id and user id.
	 * @param {Number} projectId
	 * @param {Number} userId
	 * @returns {String}
	 */
	_generateHLQRCode(projectId, userId)
	{
		return this.minter.mint('Classic', { IDProject: projectId, IDUser: userId });
	}

	/**
	 * Generates a code in the SiteManager (LADOTD) format: prefix + YY M D hh mm ss.
	 * @param {string} tmpGUIDString - The prefix (the user's SiteManager id)
	 * @param {import('headlight-qrcode/source/Headlight-QRCode-Minter.js').SiteManagerOptions} [pSiteManagerOptions]
	 * @return {string} - The QR code string
	 */
	_generateLegacyGUID(tmpGUIDString, pSiteManagerOptions)
	{
		return this.minter.mint('SiteManager', { ExternalSyncGUID: tmpGUIDString || '', TimeZone: this._getConfiguredTimeZone() }, pSiteManagerOptions || {});
	}

	/**
	 * This handles the generation of the QR code hash string based on the options passed in
	 * @param {object} [pOptions] - The options for the hash generation
	 * @return {string} - The hash string
	 */
	generateQRCodeHash(pOptions)
	{
		if (pOptions && typeof pOptions !== 'object')
		{
			throw new Error('generateQRCodeHash() was expecting an object of options, but received a non-object value.');
		}
		pOptions = pOptions || {};
		if (pOptions.LegacyFormat && pOptions.GUIDPrefix)
		{
			return this._generateLegacyGUID(pOptions.GUIDPrefix);
		}
		if (pOptions.LegacyFormat && !pOptions.GUIDPrefix)
		{
			throw new Error('generateQRCodeHash() was expecting a GUIDPrefix value for the LegacyFormat option, but received none.');
		}
		return this._generateHLQRCode(pOptions.IDProject, pOptions.IDUser);
	}

	/**
	 * Set the signed-in customer's QR settings: its `QRCodes` configuration and `CustomProperties`.
	 *
	 * @param {Object} [pConfiguration]
	 * @param {Object|string} [pCustomProperties] - Object, or the JSON string the Customer record carries.
	 * @param {number|string} [pIDCustomer] - Picks the default when nothing is configured.
	 */
	setCustomerConfiguration(pConfiguration, pCustomProperties, pIDCustomer)
	{
		let tmpProperties = pCustomProperties || {};
		if (typeof tmpProperties === 'string')
		{
			try
			{
				tmpProperties = tmpProperties.trim() ? JSON.parse(tmpProperties) : {};
			}
			catch (pError)
			{
				tmpProperties = {};
			}
		}
		this.customerProfile = libQRCodeProfiles.resolveProfile(pConfiguration || {}, tmpProperties, { IDCustomer: pIDCustomer });
	}

	/**
	 * Load the customer's QR settings once per customer, through a HeadlightRestClient-style client.
	 *
	 * Only a complete load is kept. If either read fails the profile is left unset (mintForCustomer
	 * then uses its fallback) and the next call tries again, so a network blip at sign-in can't pin a
	 * whole session to the wrong format.
	 *
	 * @param {{ getJSON: (pPath: string, fCallback: (pError: any, pResult: any) => void) => void }} pRestClient
	 * @param {number|string} pIDCustomer
	 * @return {Promise<any>} the resolved profile, or null when it couldn't be loaded
	 */
	loadCustomerConfiguration(pRestClient, pIDCustomer)
	{
		const tmpKey = String(pIDCustomer);
		if (!this.customerProfileLoads[tmpKey])
		{
			const fGet = (pPath) => new Promise((fResolve) =>
			{
				pRestClient.getJSON(pPath, (pError, pResult) => fResolve({ OK: !pError && !!pResult && !pResult.Error, Value: pResult }));
			});
			this.customerProfileLoads[tmpKey] = Promise.all([ fGet('Configuration/For/QRCodes/Profile'), fGet(`Customer/${pIDCustomer}`) ])
				.then(([ pConfiguration, pCustomer ]) =>
				{
					if (!pConfiguration.OK || !pCustomer.OK)
					{
						delete this.customerProfileLoads[tmpKey];
						if (this.fable && this.fable.log)
						{
							this.fable.log.warn(`HeadlightQRCodeHash: could not load QR settings for customer ${tmpKey}; will retry.`);
						}
						return null;
					}
					this.setCustomerConfiguration(pConfiguration.Value, pCustomer.Value.CustomProperties, pIDCustomer);
					return this.customerProfile;
				});
		}
		return this.customerProfileLoads[tmpKey];
	}

	/**
	 * @return {null|ReturnType<typeof libQRCodeProfiles.resolveProfile>} the loaded profile, if any
	 */
	getCustomerProfile()
	{
		return this.customerProfile;
	}

	/**
	 * Mint a new code in the signed-in customer's format.
	 *
	 * @param {Object} pContext
	 * @param {number} [pContext.IDProject]
	 * @param {number} [pContext.IDUser]
	 * @param {string} [pContext.ExternalSyncGUID] - SiteManager prefix; falls back to `HL<IDUser>`.
	 * @param {string} [pContext.TimeZone] - The signed-in user's time zone (SiteManager codes carry its local time); defaults to the app's configured zone.
	 * @param {number|string} [pContext.CustomerID] - Only used when the profile hasn't loaded.
	 * @return {string}
	 */
	mintForCustomer(pContext)
	{
		const tmpContext = pContext || {};
		let tmpProfile = this.customerProfile;
		if (!tmpProfile)
		{
			const tmpFallback = libQRCodeProfiles.getDefaultProfileName(tmpContext.CustomerID);
			if (this.fable && this.fable.log)
			{
				this.fable.log.warn(`HeadlightQRCodeHash: customer QR settings not loaded; using ${tmpFallback}.`);
			}
			tmpProfile = libQRCodeProfiles.resolveProfile({ Profile: tmpFallback }, {});
		}
		if (tmpProfile.CodeFormat === 'SiteManager')
		{
			const tmpPrefix = tmpContext.ExternalSyncGUID || `HL${tmpContext.IDUser || 0}`;
			return this.minter.mint('SiteManager', { ExternalSyncGUID: tmpPrefix, TimeZone: tmpContext.TimeZone || this._getConfiguredTimeZone() }, tmpProfile.SiteManager);
		}
		return this.minter.mint(tmpProfile.CodeFormat, { IDProject: tmpContext.IDProject, IDUser: tmpContext.IDUser, TimeZone: (tmpProfile.CodeFormat === 'Standard') ? this._getConfiguredTimeZone() : undefined });
	}
}

module.exports = HeadlightQRCodeHash;
